// Timezone utility - uses user-selected timezone from profile
// Default fallback to Barbados timezone if not set
const DEFAULT_TIMEZONE = 'America/Barbados';

// Get user's timezone from localStorage (cached from profile)
const getUserTimezone = () => {
  try {
    const cachedTimezone = localStorage.getItem('userTimezone');
    return cachedTimezone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

/**
 * Get current date and time in user's selected timezone
 * @returns {Date} Date object representing current time in user's timezone
 */
export const getCurrentDateInBarbados = () => {
  const timezone = getUserTimezone();
  return new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
};

/**
 * Convert any date to user's selected timezone
 * @param {Date|string} date - Date to convert
 * @returns {Date|null} Date object in user's timezone, or null if invalid
 */
export const convertToBarbadosTime = (date) => {
  if (!date) return null;
  
  try {
    const timezone = getUserTimezone();
    // Convert string to Date if needed
    let dateObj;
    if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return null;
    }
    
    // Validate the date object
    if (!dateObj || isNaN(dateObj?.getTime())) {
      return null;
    }
    
    // Convert to user's timezone using toLocaleString
    const timezoneDateString = dateObj?.toLocaleString('en-US', { timeZone: timezone });
    const convertedDate = new Date(timezoneDateString);
    
    // Validate the converted date
    if (isNaN(convertedDate?.getTime())) {
      return null;
    }
    
    return convertedDate;
  } catch (err) {
    console.error('Error converting to user timezone:', err);
    return null;
  }
};

/**
 * Format date in user's selected timezone
 * @param {Date|string} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateInBarbados = (date, options = {}) => {
  if (!date) return '';
  try {
    const timezone = getUserTimezone();
    let dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj?.getTime())) return '';
    return dateObj?.toLocaleDateString('en-US', {
      timeZone: timezone,
      ...options
    });
  } catch (err) {
    console.error('Error formatting date:', err);
    return '';
  }
};

/**
 * Format date and time in user's selected timezone
 * @param {Date|string} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date-time string
 */
export const formatDateTimeInBarbados = (date, options = {}) => {
  if (!date) return '';
  try {
    const timezone = getUserTimezone();
    let dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj?.getTime())) return '';
    return dateObj?.toLocaleString('en-US', {
      timeZone: timezone,
      ...options
    });
  } catch (err) {
    console.error('Error formatting date time:', err);
    return '';
  }
};

/**
 * Get ISO date string for user's selected timezone (YYYY-MM-DD)
 * @param {Date|string} date - Date to convert
 * @returns {string} ISO date string
 */
export const getISODateInBarbados = (date = null) => {
  try {
    let dateObj = date ? (typeof date === 'string' ? new Date(date) : date) : getCurrentDateInBarbados();
    if (!dateObj || isNaN(dateObj?.getTime())) return '';
    
    const formatted = formatDateInBarbados(dateObj, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    if (!formatted) return '';
    
    // Convert MM/DD/YYYY to YYYY-MM-DD
    const parts = formatted?.split('/');
    if (parts?.length === 3) {
      return `${parts?.[2]}-${parts?.[0]}-${parts?.[1]}`;
    }
    return formatted;
  } catch (err) {
    console.error('Error getting ISO date:', err);
    return '';
  }
};

/**
 * Get local date string in YYYY-MM-DD format without timezone conversion
 * @param {Date} date - Date object (defaults to today)
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getLocalDateString = (date = new Date()) => {
  if (!date || isNaN(date?.getTime())) return '';
  try {
    const year = date?.getFullYear();
    const month = String(date?.getMonth() + 1)?.padStart(2, '0');
    const day = String(date?.getDate())?.padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (err) {
    console.error('Error getting local date string:', err);
    return '';
  }
};

/**
 * Get start of day in user's selected timezone
 * @param {Date|string} date - Date to process
 * @returns {Date} Start of day in user's timezone
 */
export const getStartOfDayInBarbados = (date = null) => {
  try {
    let dateObj = date ? convertToBarbadosTime(date) : getCurrentDateInBarbados();
    if (!dateObj || isNaN(dateObj?.getTime())) return null;
    dateObj?.setHours(0, 0, 0, 0);
    return dateObj;
  } catch (err) {
    console.error('Error getting start of day:', err);
    return null;
  }
};

/**
 * Get end of day in user's selected timezone
 * @param {Date|string} date - Date to process
 * @returns {Date} End of day in user's timezone
 */
export const getEndOfDayInBarbados = (date = null) => {
  try {
    let dateObj = date ? convertToBarbadosTime(date) : getCurrentDateInBarbados();
    if (!dateObj || isNaN(dateObj?.getTime())) return null;
    dateObj?.setHours(23, 59, 59, 999);
    return dateObj;
  } catch (err) {
    console.error('Error getting end of day:', err);
    return null;
  }
};

/**
 * Cache user's timezone in localStorage
 * @param {string} timezone - Timezone to cache
 */
export const cacheUserTimezone = (timezone) => {
  try {
    localStorage.setItem('userTimezone', timezone);
  } catch (err) {
    console.error('Error caching timezone:', err);
  }
};

export const TIMEZONE = getUserTimezone();